import { useState } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { useToast, humanizeError } from '../shared/Toast'

const todayISO = () => new Date().toLocaleDateString('en-CA')
const nowHHMM = () => new Date().toTimeString().slice(0, 5)

export const SEVERITY = [
  { value: 'minor', label: 'Minor', hint: 'No harm done' },
  { value: 'moderate', label: 'Moderate', hint: 'Needed attention' },
  { value: 'serious', label: 'Serious', hint: 'Urgent or medical care' },
]

export const INCIDENT_TYPES = [
  { value: 'fall', label: 'Fall' },
  { value: 'seizure', label: 'Seizure' },
  { value: 'incontinence', label: 'Incontinence' },
  { value: 'wandering', label: 'Wandering' },
  { value: 'medication_error', label: 'Medication error' },
  { value: 'other', label: 'Other' },
]

export default function IncidentForm({ recipientId, onDone, onCancel }) {
  const { user } = useAuth()
  const toast = useToast()
  const [f, setF] = useState({
    incident_type: null,
    incident_date: todayISO(),
    incident_time: nowHHMM(),
    description: '',
    severity: 'minor',
    injury: false,
    injury_description: '',
    action_taken: '',
  })
  const [busy, setBusy] = useState(false)
  const set = (k, v) => setF((c) => ({ ...c, [k]: v }))

  async function save() {
    if (!f.description.trim()) return toast.error('Describe what happened.')
    setBusy(true)
    const { error } = await supabase.from('incidents').insert({
      care_recipient_id: recipientId,
      logged_by: user.id,
      incident_date: f.incident_date || todayISO(),
      incident_time: f.incident_time || null,
      incident_type: f.incident_type,
      description: f.description.trim(),
      severity: f.severity,
      injury: f.injury,
      injury_description: f.injury ? f.injury_description.trim() || null : null,
      action_taken: f.action_taken.trim() || null,
    })
    setBusy(false)
    if (error) return toast.error(humanizeError(error))
    toast.success('Incident logged.')
    onDone?.()
  }

  return (
    <div className="stack">
      <div className="spread">
        <h2 className="title" style={{ fontSize: 22 }}>Log incident</h2>
        <button
          onClick={onCancel}
          aria-label="Close"
          style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 24, lineHeight: 1 }}
        >
          ×
        </button>
      </div>

      <div className="field">
        <span className="label">Type of incident</span>
        <div className="row wrap" style={{ gap: 8 }}>
          {INCIDENT_TYPES.map((t) => (
            <button
              key={t.value}
              type="button"
              className={`chip${f.incident_type === t.value ? ' chip-on' : ''}`}
              aria-pressed={f.incident_type === t.value}
              onClick={() => set('incident_type', f.incident_type === t.value ? null : t.value)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="field">
        <label className="label" htmlFor="i-desc">What happened?</label>
        <textarea
          id="i-desc" className="textarea"
          placeholder="e.g. Slipped getting out of the bath. Caught herself on the rail, sat on the floor for a few minutes."
          value={f.description} onChange={(e) => set('description', e.target.value)}
        />
      </div>

      <div className="row" style={{ gap: 10, alignItems: 'flex-end' }}>
        <div className="field grow">
          <label className="label" htmlFor="i-date">Date</label>
          <input
            id="i-date" type="date" className="input"
            value={f.incident_date} onChange={(e) => set('incident_date', e.target.value)}
          />
        </div>
        <div className="field grow">
          <label className="label" htmlFor="i-time">Time</label>
          <input
            id="i-time" type="time" className="input"
            value={f.incident_time} onChange={(e) => set('incident_time', e.target.value)}
          />
        </div>
      </div>

      <div className="field">
        <span className="label">Severity</span>
        <div className="row wrap" style={{ gap: 8 }}>
          {SEVERITY.map((s) => (
            <button
              key={s.value}
              type="button"
              className={`chip${f.severity === s.value ? ' chip-on' : ''}`}
              aria-pressed={f.severity === s.value}
              onClick={() => set('severity', s.value)}
            >
              {s.label}
            </button>
          ))}
        </div>
        <span className="hint">
          {SEVERITY.find((s) => s.value === f.severity)?.hint}
        </span>
      </div>

      <div className="field">
        <button
          type="button"
          className={`chip${f.injury ? ' chip-on' : ''}`}
          aria-pressed={f.injury}
          onClick={() => set('injury', !f.injury)}
          style={{ alignSelf: 'flex-start' }}
        >
          {f.injury ? '✓ ' : ''}There was an injury
        </button>
      </div>

      {f.injury && (
        <div className="field">
          <label className="label" htmlFor="i-injury">Describe the injury</label>
          <textarea
            id="i-injury" className="textarea" style={{ minHeight: 72 }}
            placeholder="e.g. Bruise on left hip, small graze on forearm."
            value={f.injury_description}
            onChange={(e) => set('injury_description', e.target.value)}
          />
        </div>
      )}

      <div className="field">
        <label className="label" htmlFor="i-action">
          What was done about it? <span className="hint">— optional</span>
        </label>
        <textarea
          id="i-action" className="textarea" style={{ minHeight: 72 }}
          placeholder="e.g. Called the GP, booked a check-up for Thursday."
          value={f.action_taken} onChange={(e) => set('action_taken', e.target.value)}
        />
      </div>

      <button className="btn btn-block btn-lg" disabled={busy} onClick={save}>
        {busy ? <span className="spinner" /> : 'Save incident'}
      </button>
    </div>
  )
}
